import React from 'react';

import { Select, type SelectChangeEvent } from '@biome-design-system/ui/Select';
import { Box } from '@biome-design-system/ui/Box';
import { InputLabel } from '@biome-design-system/ui/InputLabel';
import { useIntl, type MessageDescriptor } from '@kyruus/intl';
import type { PMCConfig } from '@kyruus/types';

import { messages } from 'Src/shared/messages';
import { getDefaultCustomerSort } from 'Src/utils/search-common';

interface SortOption {
  value: string;
  descriptor_id: string;
  descriptor?: MessageDescriptor;
}

export interface SortProps {
  sort?: string;
  sortOptions: SortOption[];
  fullWidth?: boolean;
  onSortChange: (value: string) => void;
  config: PMCConfig;
  disabled: boolean;
}

export const Sort = ({
  config,
  disabled,
  fullWidth = false,
  onSortChange,
  sort,
  sortOptions
}: SortProps) => {
  const intl = useIntl();
  const getOptionLabel = (option: SortOption) => {
    const descriptor = (option.descriptor = messages[
      option.descriptor_id as keyof typeof messages
    ] || {
      id: `sortdropdown.${option.descriptor_id}`,
      defaultMessage: option.descriptor_id,
      description: `Option for ${option.descriptor_id} sort`
    });

    return intl.formatMessage(descriptor);
  };

  const handleOnChange = (event?: SelectChangeEvent) => {
    const sortValue = String(event?.target.value ?? '');
    onSortChange(sortValue);
  };

  const defaultSort = getDefaultCustomerSort(config);
  const currentValue = sort ?? defaultSort;

  // If the applied sort param is not a configured option, default to unselected.
  const isValidValue = sortOptions.some(
    (option) => option.value === currentValue
  );
  const safeValue = isValidValue ? currentValue : undefined;

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        textTransform: 'capitalize'
      }}
    >
      <InputLabel
        disabled={disabled}
        htmlFor="sort-select"
        id="sort-select-label"
        sx={(theme) => ({
          textIndent: 'unset',
          transform: 'unset',
          fontWeight: 'normal',
          fontSize: theme.typography.fontSize,
          mb: 0
        })}
      >
        {intl.formatMessage(messages.sortby)}
      </InputLabel>
      <Select
        aria-labelledby="sort-select-label"
        data-testid="SortMenu"
        placeholder={intl.formatMessage(messages.sortbyPlaceholder)}
        disabled={disabled}
        fullWidth={fullWidth}
        getOptionKey={(o) => o.descriptor_id}
        getOptionLabel={getOptionLabel}
        getOptionValue={(o) => o.value}
        id="sort-select"
        name="sort-select"
        onChange={handleOnChange}
        options={sortOptions}
        size="small"
        sx={{
          '.MuiSelect-select.MuiInputBase-input': {
            minWidth: 100
          }
        }}
        value={safeValue}
      />
    </Box>
  );
};
